import { useRef, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Icons, QS_GLYPH, ic } from "../wizard-icons";
import type { BrandColors, ShippingRow } from "../wizard-types";
import {
  IMAGE_ACCEPT,
  downscaleImageToDataUrl,
  validateWizardImage,
} from "../wizard-image";
import { BrandColorsPanel } from "./BrandColorsPanel";
import { GovernoratePickerSheet } from "./GovernoratePickerSheet";

const K = keys.quickSetup;

const errorBox: CSSProperties = {
  marginTop: 10,
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--danger)",
};

export type Governorate = { id: string; name: string };

// Sensible starting values for a freshly-added shipping area so the seller
// never lands on an empty price/days field — they can tweak or bulk-apply.
const DEFAULT_SHIP_PRICE = "50";
const DEFAULT_SHIP_DAYS = "3";

// ── Logo upload (step 1) ─────────────────────────────────────────
// Matches the handoff's dashed drop-zone: camera tile, "Drag your logo here,
// or browse", format hint, outline "+ Upload logo" button. Continue is
// always enabled (skipping is a first-class path, not a disabled state) and
// the skip line ("No logo yet? … Skip for now") advances without a logo.
export function LogoUpload({
  value,
  onChange,
  onDone,
  brandColors,
  onExtract,
  onSelectAccent,
  onNeutral,
  narrow,
}: {
  value: string | null;
  onChange: (v: string) => void;
  onDone: () => void;
  /** Confirmed brand colors (Phase 3) — non-null after a successful extraction
   *  or a neutral choice. When set, the confirmation panel replaces the upload
   *  zone; when null the step behaves exactly as before (skip / no extraction). */
  brandColors: BrandColors | null;
  /** Fired after a successful upload/downscale with the data URL + original
   *  filename, so the wizard can run `extractPalette` and set `brandColors`. */
  onExtract: (dataUrl: string, filename: string) => void;
  /** Re-select the accent swatch (recomputes derived tokens in the wizard). */
  onSelectAccent: (hex: string) => void;
  /** Fall back to the neutral/default theme (collapses the panel). */
  onNeutral: () => void;
  narrow: boolean;
}): ReactElement {
  const t = useTranslations();
  const fileRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const read = (f?: File) => {
    if (!f) return;
    const err = validateWizardImage(f);
    if (err) {
      setError(err === "type" ? t(K.errors.imageType) : t(K.errors.imageSize));
      return;
    }
    setError(null);
    const filename = f.name;
    // Downscale before handing the data URL up — keeps the logo well under any
    // upload size limit so it can never trigger a backend "File too large".
    downscaleImageToDataUrl(f, 512)
      .then((dataUrl) => {
        // Paint the preview first; the wizard runs extraction asynchronously
        // (<200ms) after this so the confirmation panel appears right after.
        onChange(dataUrl);
        onExtract(dataUrl, filename);
      })
      .catch(() => setError(t(K.errors.imageType)));
  };
  // Once colors are confirmed (or neutral chosen), the upload zone is replaced
  // by the confirmation panel. Extraction failure / skip leaves brandColors null
  // → this branch is skipped and the original upload UI renders unchanged.
  if (value && brandColors) {
    return (
      <>
        <BrandColorsPanel
          logo={value}
          filename={brandColors.filename}
          palette={brandColors.palette}
          selectedAccent={brandColors.accent}
          derived={brandColors.derived}
          theme={brandColors.theme}
          narrow={narrow}
          onSelectAccent={onSelectAccent}
          onNeutral={onNeutral}
          onReplace={() => fileRef.current?.click()}
          onContinue={onDone}
        />
        <input
          ref={fileRef}
          type="file"
          accept={IMAGE_ACCEPT}
          onChange={(e) => read(e.target.files?.[0])}
          style={{ display: "none" }}
        />
      </>
    );
  }
  return (
    <div style={{ animation: "qs-in .3s ease", display: "flex", flexDirection: "column", gap: 20 }}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          read(e.dataTransfer.files[0]);
        }}
        style={{
          border: `2px dashed ${drag ? "var(--accent)" : "var(--dashed)"}`,
          borderRadius: 16,
          background: drag ? "color-mix(in oklch, var(--accent) 8%, white)" : "var(--surface)",
          padding: 48,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 14,
          transition: "all .15s",
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            flexShrink: 0,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: value ? "#fff" : "var(--navy)",
            color: "var(--accent)",
            border: value ? "1px solid var(--border)" : "none",
          }}
        >
          {value ? (
            <img src={value} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            ic(Icons.camera, 26, { stroke: 1.6 })
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, textAlign: "center" }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
            {value ? t(K.logo.hintAdded) : t(K.logo.hint)}
          </span>
          <span style={{ fontSize: 13, color: "var(--fine)" }}>{t(K.logo.hintFormat)}</span>
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          style={{
            height: 44,
            padding: "0 24px",
            borderRadius: 10,
            border: "1.5px solid var(--navy)",
            background: "#fff",
            color: "var(--navy)",
            fontSize: 14,
            fontWeight: 800,
            fontFamily: "inherit",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          {ic(value ? Icons.edit : Icons.plus, 15)}
          {value ? t(K.logo.replace) : t(K.logo.uploadBtn)}
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={IMAGE_ACCEPT}
        onChange={(e) => read(e.target.files?.[0])}
        style={{ display: "none" }}
      />
      {error && <div style={errorBox}>{error}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button
          onClick={onDone}
          style={{
            width: "100%",
            padding: "13px 16px",
            borderRadius: 10,
            border: "none",
            background: "var(--accent)",
            color: "var(--navy)",
            fontSize: 16,
            fontWeight: 800,
            cursor: "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {t(K.logo.continue)}
        </button>
        <span style={{ fontSize: 13, color: "var(--fine)", textAlign: "center" }}>
          {t(K.logo.skipLine)}{" "}
          <button
            onClick={onDone}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              fontWeight: 700,
              color: "var(--navy)",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {t(K.logo.skipCta)}
          </button>
        </span>
      </div>
    </div>
  );
}

// ── Shipping form ────────────────────────────────────────────────
export function ShippingForm({
  governorates,
  initial,
  onDone,
  narrow = false,
}: {
  governorates: Governorate[];
  initial: ShippingRow[];
  onDone: (rows: ShippingRow[]) => void;
  narrow?: boolean;
}): ReactElement {
  const t = useTranslations();
  // Tighter numeric columns on mobile so long governorate names get more room
  // (they otherwise ellipsize hard on a 390px viewport).
  const cols = narrow ? "1fr 56px 56px 30px" : "1fr 76px 76px 32px";
  const seed = (): ShippingRow[] => {
    if (initial.length) return initial;
    const pick = (
      names: string[],
      price: string,
      days: string,
    ): ShippingRow | null => {
      const g = governorates.find((x) =>
        names.some((n) => x.name.toLowerCase() === n.toLowerCase()),
      );
      return g ? { govId: g.id, gov: g.name, price, days } : null;
    };
    return [
      pick(["Cairo", "القاهرة"], "60", "3"),
      pick(["Alexandria", "الإسكندرية"], "75", "4"),
    ].filter((r): r is ShippingRow => r !== null);
  };
  const [rows, setRows] = useState<ShippingRow[]>(seed);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Bulk "apply to all" values — one price + delivery time pushed to every row.
  const [bulkPrice, setBulkPrice] = useState(DEFAULT_SHIP_PRICE);
  const [bulkDays, setBulkDays] = useState(DEFAULT_SHIP_DAYS);
  const usedIds = rows.map((r) => r.govId);
  // Reconcile the picker's confirmed selection into the priced rows: keep the
  // price/time already set for governorates that stay selected, add new ones at
  // the defaults, and drop deselected ones — ordered by the canonical
  // governorate list so the table order stays stable regardless of tap order.
  const applySelection = (ids: string[]) => {
    const idSet = new Set(ids);
    setRows((prev) => {
      const byId = new Map(prev.map((r) => [r.govId, r] as const));
      return governorates
        .filter((g) => idSet.has(g.id))
        .map(
          (g) =>
            byId.get(g.id) ?? {
              govId: g.id,
              gov: g.name,
              price: DEFAULT_SHIP_PRICE,
              days: DEFAULT_SHIP_DAYS,
            },
        );
    });
  };
  const remove = (id: string) =>
    setRows((r) => r.filter((x) => x.govId !== id));
  const update = (id: string, k: "price" | "days", v: string) =>
    setRows((r) =>
      r.map((x) =>
        x.govId === id ? { ...x, [k]: v.replace(/[^0-9]/g, "") } : x,
      ),
    );
  // Push the bulk price/days onto every current row (falls back to sane
  // defaults if a field was cleared) so the seller can set them all at once.
  const applyToAll = () =>
    setRows((r) =>
      r.map((x) => ({
        ...x,
        price: bulkPrice || DEFAULT_SHIP_PRICE,
        days: bulkDays || DEFAULT_SHIP_DAYS,
      })),
    );
  // Every area needs a price before finishing (days defaults to 1 server-side).
  const ready = rows.length > 0 && rows.every((r) => r.price.trim() !== "");
  const inp: CSSProperties = {
    width: narrow ? 48 : 64,
    padding: narrow ? "8px 6px" : "8px 10px",
    borderRadius: 8,
    border: "1px solid var(--border-strong)",
    fontSize: 13.5,
    fontFamily: "'JetBrains Mono', monospace",
    color: "var(--text)",
    textAlign: "center",
    background: "var(--surface)",
  };

  return (
    <div
      style={{
        animation: "qs-in .3s ease",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 16,
        padding: 18,
        boxShadow: "0 1px 2px rgba(15,20,40,0.04)",
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 12 }}>
        {t(K.shipping.areas)}
      </div>
      {/* Opens the responsive governorate picker (bottom-sheet on mobile /
          modal on desktop). Selection lives there; pricing stays here. */}
      <button
        onClick={() => setPickerOpen(true)}
        style={{
          width: "100%",
          marginBottom: rows.length ? 14 : 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: "12px 16px",
          borderRadius: 12,
          border: "1.5px dashed var(--border-strong)",
          background: "var(--bg-soft)",
          color: "var(--text-2)",
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        {ic(Icons.plus, 16)}
        {t(rows.length ? K.shipping.editAreas : K.shipping.addAreas)}
      </button>
      {rows.length > 0 && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              padding: "11px 12px",
              marginBottom: 14,
              borderRadius: 11,
              background: "var(--bg-soft)",
              border: "1px solid var(--border)",
            }}
          >
            <span
              style={{
                flex: "1 1 130px",
                minWidth: 0,
                fontSize: 12.5,
                fontWeight: 600,
                color: "var(--text-2)",
              }}
            >
              {t(K.shipping.applyToAllTitle)}
            </span>
            {/* Keep price + days + apply together as one group so they wrap
                as a unit below the title on mobile (clean 2-row layout)
                instead of scattering across 3–4 ragged lines. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <input
                value={bulkPrice}
                onChange={(e) =>
                  setBulkPrice(e.target.value.replace(/[^0-9]/g, ""))
                }
                placeholder={t(K.shipping.priceHeader)}
                aria-label={t(K.shipping.priceHeader)}
                style={inp}
              />
              <input
                value={bulkDays}
                onChange={(e) =>
                  setBulkDays(e.target.value.replace(/[^0-9]/g, ""))
                }
                placeholder={t(K.shipping.daysHeader)}
                aria-label={t(K.shipping.daysHeader)}
                style={inp}
              />
              <button
                onClick={applyToAll}
                style={{
                  flexShrink: 0,
                  padding: "8px 14px",
                  borderRadius: 9,
                  border: "none",
                  background: "var(--navy)",
                  color: "#fff",
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                {t(K.shipping.applyToAllBtn)}
              </button>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: cols,
              gap: 10,
              padding: "0 4px 6px",
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "var(--text-3)",
            }}
          >
            <span>{t(K.shipping.govHeader)}</span>
            <span style={{ textAlign: "center" }}>
              {t(K.shipping.priceHeader)}
            </span>
            <span style={{ textAlign: "center" }}>
              {t(K.shipping.daysHeader)}
            </span>
            <span />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              // Desktop: cap the list so "Add all" (~40 governorates) scrolls
              // inside a bounded region instead of a 2,000px+ wall. Mobile: no
              // inner cap — the whole step already scrolls in the one outer
              // QuestionScreen scroll region, so an inner scroll here would
              // create a nested/double scrollbar and strand the heading.
              maxHeight: narrow ? undefined : "min(52vh, 420px)",
              overflowY: narrow ? "visible" : "auto",
              overscrollBehavior: "contain",
              paddingInlineEnd: narrow ? 0 : 4,
            }}
          >
            {rows.map((r) => (
              <div
                key={r.govId}
                style={{
                  display: "grid",
                  gridTemplateColumns: cols,
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: 0,
                    padding: "9px 12px",
                    borderRadius: 9,
                    background: "var(--bg-soft)",
                    border: "1px solid var(--border)",
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "var(--text)",
                  }}
                >
                  <span style={{ color: "var(--accent-2)", display: "flex", flexShrink: 0 }}>
                    {ic(QS_GLYPH.pin, 14)}
                  </span>
                  {/* Truncate to one line so every row is a uniform height. On
                      desktop the column is wide enough that nothing clips; on
                      mobile long names ellipsize instead of wrapping to 2–3
                      lines and making the rows ragged. */}
                  <span
                    style={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.gov}
                  </span>
                </div>
                <input
                  value={r.price}
                  onChange={(e) => update(r.govId, "price", e.target.value)}
                  placeholder="—"
                  style={inp}
                />
                <input
                  value={r.days}
                  onChange={(e) => update(r.govId, "days", e.target.value)}
                  placeholder="—"
                  style={inp}
                />
                <button
                  onClick={() => remove(r.govId)}
                  aria-label={t(K.shipping.removeAria, { name: r.gov })}
                  style={{
                    width: narrow ? 30 : 32,
                    height: narrow ? 30 : 32,
                    borderRadius: 8,
                    border: "1px solid var(--border)",
                    background: "var(--surface)",
                    color: "var(--text-3)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {ic(Icons.trash, 14)}
                </button>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 12 }}>
            {t(rows.length > 1 ? K.shipping.hintMany : K.shipping.hintOne, {
              count: rows.length,
            })}
          </div>
        </>
      )}
      <button
        onClick={() => ready && onDone(rows)}
        disabled={!ready}
        style={{
          marginTop: 16,
          width: "100%",
          padding: "13px 16px",
          borderRadius: 12,
          border: "none",
          background: ready ? "var(--accent)" : "var(--border-strong)",
          color: ready ? "var(--on-accent)" : "var(--text-3)",
          fontSize: 14.5,
          fontWeight: 700,
          cursor: ready ? "pointer" : "not-allowed",
          fontFamily: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        {/* The copy (K.shipping.finish = "Finish setup ✓") already carries the
            check glyph — matches the nationwide button; no extra icon here. */}
        {rows.length && !ready ? t(K.shipping.needPrice) : t(K.shipping.finish)}
      </button>

      <GovernoratePickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        governorates={governorates}
        selectedIds={usedIds}
        onConfirm={applySelection}
      />
    </div>
  );
}

// Payment capture for the wizard now lives in WizardPaymentStep.tsx, which
// reuses the Settings payment building blocks so both surfaces stay identical.

// ── WhatsApp number capture ──────────────────────────────────────
// Validate on tap rather than disabling the button: the seller can always
// press Continue, and only sees the inline "enter a valid number" error if
// the current input doesn't match `^01\d{9}$` yet.
export function WhatsAppStep({
  value,
  onChange,
  onDone,
  onSkip,
}: {
  value: string;
  onChange: (v: string) => void;
  onDone: () => void;
  onSkip: () => void;
}): ReactElement {
  const t = useTranslations();
  const [touched, setTouched] = useState(false);
  // Normalize to the local Egyptian format and require it to be valid
  // (01XXXXXXXXX) — the same shape the backend stores.
  let local = value.replace(/[^0-9]/g, "");
  if (local.startsWith("20")) local = local.slice(2);
  if (local.length === 10 && local.startsWith("1")) local = "0" + local;
  const ready = /^01\d{9}$/.test(local);
  const showInvalid = touched && value.trim().length > 0 && !ready;

  const handleContinue = () => {
    setTouched(true);
    if (ready) onDone();
  };

  return (
    <div style={{ animation: "qs-in .3s ease", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Phone entry stays LTR in every locale (+20 prefix on the left, digits
          left-to-right) — a mirrored phone field reads wrong in Arabic. */}
      <div
        dir="ltr"
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 0,
          border: `2px solid ${ready ? "var(--ok)" : showInvalid ? "var(--danger)" : "var(--border-strong)"}`,
          borderRadius: 12,
          overflow: "hidden",
          background: "var(--surface)",
          height: 56,
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "0 16px",
            background: "var(--chip-bg)",
            borderInlineEnd: "1px solid var(--divider-2)",
            fontSize: 15,
            fontWeight: 700,
            color: "var(--navy)",
            whiteSpace: "nowrap",
            direction: "ltr",
          }}
        >
          <span style={{ color: "var(--wa-green)", display: "flex" }}>
            {ic(QS_GLYPH.whats, 19)}
          </span>
          {t(K.whatsapp.prefix)}
        </span>
        <input
          value={value}
          onChange={(e) => {
            setTouched(false);
            onChange(e.target.value);
          }}
          inputMode="tel"
          placeholder={t(K.whatsapp.placeholder)}
          autoFocus
          dir="ltr"
          onBlur={() => setTouched(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleContinue();
          }}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            padding: "0 16px",
            fontSize: 17,
            letterSpacing: 0.5,
            fontFamily: "'JetBrains Mono', monospace",
            color: "var(--navy)",
            background: "transparent",
          }}
        />
        {ready && (
          <span style={{ padding: "0 16px", display: "flex", alignItems: "center", color: "var(--ok)", fontWeight: 700 }}>
            {ic(Icons.check, 15, { stroke: 2.6 })}
          </span>
        )}
      </div>
      {ready ? (
        <span style={{ fontSize: 13, color: "var(--ok)", fontWeight: 600 }}>{t(K.whatsapp.valid)}</span>
      ) : showInvalid ? (
        <span style={{ fontSize: 13, color: "var(--danger)", fontWeight: 600 }}>{t(K.whatsapp.invalid)}</span>
      ) : null}
      <span style={{ fontSize: 13, color: "var(--fine)", lineHeight: 1.5 }}>{t(K.whatsapp.deferredNote)}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button
          onClick={handleContinue}
          style={{
            width: "100%",
            padding: "13px 16px",
            borderRadius: 10,
            border: "none",
            background: "var(--accent)",
            color: "var(--navy)",
            fontSize: 16,
            fontWeight: 800,
            cursor: "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {t(K.whatsapp.continue)}
        </button>
        <button
          onClick={onSkip}
          style={{
            width: "100%",
            padding: "8px",
            background: "transparent",
            border: "none",
            color: "var(--fine)",
            fontSize: 13,
            fontWeight: 700,
            textDecoration: "underline",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t(K.whatsapp.addLater)}
        </button>
      </div>
    </div>
  );
}
